import { useRef } from 'react';

import Add from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, IconButton, Stack, Typography, useTheme } from '@mui/material';

import { OfferProduct, PricingServiceTypes, PricingTerms } from '../ServiceType';

export type OrderType = 'primary' | 'secondary';

interface ServiceProductsProps {
    orderType: OrderType;
    products: OfferProduct[];
    selectProduct: (id: number, type: OrderType) => void;
    selectedProducts: OfferProduct[];
    serviceType: PricingServiceTypes;
    contractTerm: PricingTerms;
    partnerId: number;
    disabled: boolean;
}

export const ServiceProducts: React.FC<ServiceProductsProps> = ({
    orderType,
    products,
    selectProduct,
    selectedProducts,
    serviceType,
    disabled,
}) => {
    const theme = useTheme();

    const containerRef = useRef(null);

    const handleScroll = (direction: string) => {
        if (direction === 'left') {
            containerRef.current.scrollLeft += -containerRef.current.clientWidth;
        } else {
            containerRef.current.scrollLeft += containerRef.current.clientWidth;
        }
    };

    const isProductSelected = (id: number) => {
        return selectedProducts?.[orderType]?.some((product) => {
            return product?.id === id;
        });
    };

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            my={2}
        >
            <IconButton
                sx={{
                    bgcolor: '#000',
                    color: '#fff',
                    '&:hover': {
                        bgcolor: '#000',
                        color: '#fff',
                        opacity: 0.8,
                    },
                }}
                onClick={() => {
                    return handleScroll('left');
                }}
                disabled={disabled}
            >
                <ChevronLeftIcon />
            </IconButton>
            <Box
                ref={containerRef}
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    scrollbarColor: 'transparent transparent',
                    '& > *:not(:last-child)': {
                        marginRight: '16px',
                    },
                    transform: 'translateZ(0)',
                }}
            >
                {products.map((product) => {
                    return (
                        <Box
                            key={product.id}
                            sx={{
                                minWidth: '216px',
                                height: '278px',
                                borderRadius: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                border: `1px solid ${theme.palette.primary.main}`,
                                padding: '16px',
                                alignItems: 'center',
                                gap: '16px',
                                boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                            }}
                        >
                            <Box sx={{ flexGrow: 1 }}>
                                <img
                                    src={product.imageUrl}
                                    alt={product.name}
                                    height={148}
                                    width={160}
                                    style={{ objectFit: 'contain' }}
                                />
                            </Box>

                            <Typography fontSize="18px" color="#667085">
                                {product.name}
                            </Typography>

                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={isProductSelected(product.id) ? null : <Add />}
                                sx={{
                                    bgcolor: '#000',
                                    color: '#fff',
                                    '&:hover': {
                                        bgcolor: '#000',
                                        color: '#fff',
                                        opacity: 0.8,
                                    },
                                    textTransform: 'uppercase',
                                }}
                                onClick={() => {
                                    return selectProduct(product.id, orderType);
                                }}
                                disabled={
                                    isProductSelected(product.id) ||
                                    (selectedProducts?.length > 0 &&
                                        serviceType === PricingServiceTypes.ONLINE &&
                                        orderType === 'primary') ||
                                    disabled
                                }
                            >
                                {isProductSelected(product.id) ? 'Added' : 'Add'}
                            </Button>
                        </Box>
                    );
                })}
            </Box>
            <IconButton
                sx={{
                    bgcolor: '#000',
                    color: '#fff',
                    '&:hover': {
                        bgcolor: '#000',
                        color: '#fff',
                        opacity: 0.8,
                    },
                }}
                onClick={() => {
                    return handleScroll('right');
                }}
                disabled={disabled}
            >
                <ChevronRightIcon />
            </IconButton>
        </Stack>
    );
};
